import { useState, type FormEvent } from 'react';
import type { Widget, WidgetType } from '@detect/shared';
import { Modal, ErrorState } from '../../components/common';
import { usePickerOptions } from '../../hooks/usePickerOptions';
import { resourceService } from '../../services/resources';
import { sensorService } from '../../services/sensors';

/**
 * Widget editor: a pragmatic per-type form over the strict widget-data
 * contracts. Metric series are entered as resource + sensorKey +
 * datapoint (the sensor library ships in M14; free-text keys keep this
 * usable now). Layout is numeric (drag-and-drop is a later
 * enhancement; the grid contract is already position-based).
 */

interface SeriesDraft {
  resourceId: string;
  sensorKey: string;
  instanceId: string;
  datapoint: string;
  label: string;
}

const EMPTY_SERIES: SeriesDraft = {
  resourceId: '',
  sensorKey: '',
  instanceId: '',
  datapoint: '',
  label: '',
};

function seriesToConfig(s: SeriesDraft) {
  return {
    resourceId: s.resourceId,
    sensorKey: s.sensorKey,
    instanceId: s.instanceId || null,
    datapoint: s.datapoint,
    ...(s.label ? { label: s.label } : {}),
  };
}

const WIDGET_TYPES_UI: { value: WidgetType; label: string }[] = [
  { value: 'line_graph', label: 'Line graph' },
  { value: 'area_graph', label: 'Area graph' },
  { value: 'big_number', label: 'Big number' },
  { value: 'gauge', label: 'Gauge' },
  { value: 'table', label: 'Value table' },
  { value: 'alert_list', label: 'Alert list' },
  { value: 'noc_status', label: 'NOC status grid' },
  { value: 'sla', label: 'SLA / availability' },
  { value: 'website_status', label: 'Website status' },
  { value: 'text', label: 'Text' },
];

export function WidgetModal({
  widget,
  nextY,
  onClose,
  onSave,
}: {
  widget: Widget | null;
  /** Grid row to place a NEW widget at (below existing content). */
  nextY: number;
  onClose: () => void;
  onSave: (widget: Widget) => Promise<void>;
}) {
  const [type, setType] = useState<WidgetType>(widget?.type ?? 'line_graph');
  const [title, setTitle] = useState(widget?.title ?? '');
  const [series, setSeries] = useState<SeriesDraft[]>(() => {
    const existing = (widget?.config.series ?? null) as
      | Record<string, unknown>[]
      | Record<string, unknown>
      | null;
    if (!existing) return [{ ...EMPTY_SERIES }];
    const list = Array.isArray(existing) ? existing : [existing];
    return list.map((s) => ({
      resourceId: String(s.resourceId ?? ''),
      sensorKey: String(s.sensorKey ?? ''),
      instanceId: s.instanceId ? String(s.instanceId) : '',
      datapoint: String(s.datapoint ?? ''),
      label: s.label ? String(s.label) : '',
    }));
  });
  const [gaugeMin, setGaugeMin] = useState(String(widget?.config.min ?? '0'));
  const [gaugeMax, setGaugeMax] = useState(String(widget?.config.max ?? '100'));
  const [minSeverity, setMinSeverity] = useState(
    String(widget?.config.minSeverity ?? 'warning')
  );
  const [nocClientId, setNocClientId] = useState(String(widget?.config.clientId ?? ''));
  const [nocGroup, setNocGroup] = useState(String(widget?.config.group ?? ''));
  const [slaResources, setSlaResources] = useState<string[]>(
    Array.isArray(widget?.config.resourceIds)
      ? (widget?.config.resourceIds as string[])
      : []
  );
  const [slaTarget, setSlaTarget] = useState(String(widget?.config.targetPct ?? '99.9'));
  const [markdown, setMarkdown] = useState(String(widget?.config.markdown ?? ''));
  const [width, setWidth] = useState(String(widget?.position.w ?? 24));
  const [height, setHeight] = useState(String(widget?.position.h ?? 8));
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const resourcesQuery = usePickerOptions(['resources'], (tid, startAfter) =>
    resourceService.list(tid, { limit: 100, startAfter })
  );
  // Sensor picker (#PO 2026-07-21): free-text keys predate the library
  // and made empty-series typos invisible — pick from the tenant's real
  // sensors; datapoints narrow to the chosen sensor.
  const sensorsQuery = usePickerOptions(['sensors'], (tid, startAfter) =>
    sensorService.list(tid, { limit: 100, startAfter })
  );
  const sensors = sensorsQuery.data?.items ?? [];
  const resources = resourcesQuery.data?.items ?? [];

  const needsSeries = ['line_graph', 'area_graph', 'big_number', 'gauge', 'table'].includes(
    type
  );
  const singleSeries = type === 'big_number' || type === 'gauge';

  const buildConfig = (): Record<string, unknown> => {
    // MERGE over the existing config: the widget update replaces the
    // whole object, and this form exposes only the common fields —
    // API-configured extras (bucketSeconds, aggregation, warn/crit
    // thresholds, units, list filters, windowDays…) must round-trip.
    // Fields the form DOES expose get explicit set-or-clear handling.
    const base: Record<string, unknown> = widget ? { ...widget.config } : {};
    switch (type) {
      case 'line_graph':
      case 'area_graph':
      case 'table':
        return { ...base, series: series.map(seriesToConfig) };
      case 'big_number':
        return { ...base, series: seriesToConfig(series[0]) };
      case 'gauge':
        return {
          ...base,
          series: seriesToConfig(series[0]),
          min: Number(gaugeMin),
          max: Number(gaugeMax),
        };
      case 'alert_list':
        return { ...base, minSeverity };
      case 'noc_status': {
        const config = { ...base };
        if (nocClientId) config.clientId = nocClientId;
        else delete config.clientId;
        if (nocGroup) config.group = nocGroup;
        else delete config.group;
        return config;
      }
      case 'sla':
        return { ...base, resourceIds: slaResources, targetPct: Number(slaTarget) };
      case 'website_status':
        return { ...base, resourceIds: slaResources };
      case 'text':
        return { ...base, markdown };
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSave({
        id: widget?.id ?? `w_${crypto.randomUUID().slice(0, 8)}`,
        type,
        title,
        position: widget?.position ?? {
          x: 0,
          y: nextY,
          w: Math.min(48, Math.max(1, Number(width))),
          h: Math.min(100, Math.max(1, Number(height))),
        },
        config: buildConfig(),
      });
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  };

  const setOneSeries = (index: number, patch: Partial<SeriesDraft>) => {
    setSeries((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  return (
    <Modal title={widget ? `Edit ${widget.title}` : 'Add widget'} onClose={onClose} wide>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="widget-type">Type</label>
          <select
            id="widget-type"
            name="type"
            value={type}
            disabled={widget !== null}
            onChange={(e) => setType(e.target.value as WidgetType)}
          >
            {WIDGET_TYPES_UI.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="widget-title">Title</label>
          <input
            id="widget-title"
            name="title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {needsSeries &&
          (singleSeries ? series.slice(0, 1) : series).map((s, i) => (
            <fieldset key={i} className="stage-editor">
              <legend>
                Series {i + 1}
                {!singleSeries && series.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setSeries((prev) => prev.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                )}
              </legend>
              <div className="form-field">
                <label htmlFor={`series-${i}-resource`}>Resource</label>
                <select
                  id={`series-${i}-resource`}
                  name={`series-${i}-resource`}
                  required
                  value={s.resourceId}
                  onChange={(e) => setOneSeries(i, { resourceId: e.target.value })}
                >
                  <option value="">— select —</option>
                  {resources.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.displayName ?? r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label htmlFor={`series-${i}-sensor`}>Sensor</label>
                <select
                  id={`series-${i}-sensor`}
                  name={`series-${i}-sensor`}
                  required
                  value={s.sensorKey}
                  onChange={(e) =>
                    setOneSeries(i, { sensorKey: e.target.value, datapoint: '' })
                  }
                >
                  <option value="">— select —</option>
                  {sensors.map((sensor) => (
                    <option key={sensor.id} value={sensor.key}>
                      {sensor.displayName} ({sensor.key})
                    </option>
                  ))}
                  {s.sensorKey &&
                    !sensors.some((sensor) => sensor.key === s.sensorKey) && (
                      <option value={s.sensorKey}>{s.sensorKey}</option>
                    )}
                </select>
              </div>
              <div className="form-field">
                <label htmlFor={`series-${i}-datapoint`}>Datapoint</label>
                <select
                  id={`series-${i}-datapoint`}
                  name={`series-${i}-datapoint`}
                  required
                  value={s.datapoint}
                  onChange={(e) => setOneSeries(i, { datapoint: e.target.value })}
                >
                  <option value="">— select —</option>
                  {(
                    sensors.find((sensor) => sensor.key === s.sensorKey)
                      ?.datapoints ?? []
                  ).map((dp) => (
                    <option key={dp.name} value={dp.name}>
                      {dp.name}
                    </option>
                  ))}
                  {s.datapoint &&
                    !(
                      sensors.find((sensor) => sensor.key === s.sensorKey)
                        ?.datapoints ?? []
                    ).some((dp) => dp.name === s.datapoint) && (
                      <option value={s.datapoint}>{s.datapoint}</option>
                    )}
                </select>
              </div>
              <div className="form-field">
                <label htmlFor={`series-${i}-label`}>Label (optional)</label>
                <input
                  id={`series-${i}-label`}
                  name={`series-${i}-label`}
                  value={s.label}
                  onChange={(e) => setOneSeries(i, { label: e.target.value })}
                />
              </div>
            </fieldset>
          ))}
        {needsSeries && !singleSeries && series.length < 10 && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setSeries((prev) => [...prev, { ...EMPTY_SERIES }])}
          >
            Add series
          </button>
        )}

        {type === 'gauge' && (
          <>
            <div className="form-field">
              <label htmlFor="gauge-min">Gauge min</label>
              <input
                id="gauge-min"
                name="min"
                type="number"
                required
                value={gaugeMin}
                onChange={(e) => setGaugeMin(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="gauge-max">Gauge max</label>
              <input
                id="gauge-max"
                name="max"
                type="number"
                required
                value={gaugeMax}
                onChange={(e) => setGaugeMax(e.target.value)}
              />
            </div>
          </>
        )}

        {type === 'alert_list' && (
          <div className="form-field">
            <label htmlFor="widget-severity">Minimum severity</label>
            <select
              id="widget-severity"
              name="minSeverity"
              value={minSeverity}
              onChange={(e) => setMinSeverity(e.target.value)}
            >
              <option value="warning">warning</option>
              <option value="error">error</option>
              <option value="critical">critical</option>
            </select>
          </div>
        )}

        {type === 'noc_status' && (
          <>
            <div className="form-field">
              <label htmlFor="noc-client">Client id (optional narrow)</label>
              <input
                id="noc-client"
                name="clientId"
                value={nocClientId}
                onChange={(e) => setNocClientId(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="noc-group">Group (optional narrow)</label>
              <input
                id="noc-group"
                name="group"
                value={nocGroup}
                onChange={(e) => setNocGroup(e.target.value)}
              />
            </div>
          </>
        )}

        {(type === 'sla' || type === 'website_status') && (
          <div className="form-field">
            <label htmlFor="widget-resources">Resources</label>
            <select
              id="widget-resources"
              name="resourceIds"
              multiple
              required
              size={5}
              value={slaResources}
              onChange={(e) =>
                setSlaResources(
                  Array.from(e.target.selectedOptions).map((o) => o.value)
                )
              }
            >
              {resources
                .filter((r) => type !== 'website_status' || r.kind === 'website')
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.displayName ?? r.name}
                  </option>
                ))}
            </select>
          </div>
        )}
        {type === 'sla' && (
          <div className="form-field">
            <label htmlFor="sla-target">Target %</label>
            <input
              id="sla-target"
              name="targetPct"
              type="number"
              min={0}
              max={100}
              step={0.01}
              value={slaTarget}
              onChange={(e) => setSlaTarget(e.target.value)}
            />
          </div>
        )}

        {type === 'text' && (
          <div className="form-field">
            <label htmlFor="widget-markdown">Text</label>
            <textarea
              id="widget-markdown"
              name="markdown"
              rows={4}
              required
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
            />
          </div>
        )}

        {!widget && (
          <>
            <div className="form-field">
              <label htmlFor="widget-width">Width (grid columns, 1–48)</label>
              <input
                id="widget-width"
                name="width"
                type="number"
                min={1}
                max={48}
                value={width}
                onChange={(e) => setWidth(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="widget-height">Height (grid rows)</label>
              <input
                id="widget-height"
                name="height"
                type="number"
                min={1}
                max={100}
                value={height}
                onChange={(e) => setHeight(e.target.value)}
              />
            </div>
          </>
        )}

        <div className="modal-footer">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : widget ? 'Save widget' : 'Add widget'}
          </button>
        </div>
        {resourcesQuery.data?.truncated && (
          <p className="text-muted">Resource list truncated.</p>
        )}
      </form>
    </Modal>
  );
}
